import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LegalPages.css';

// Iconos
const AlertTriangleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

// Componente de aviso destacado
const AvisoDestacado = () => (
  <div className="aviso-destacado">
    <AlertTriangleIcon />
    <div>
      <strong>Verificación obligatoria por el usuario</strong>
      <p>
        Esta herramienta realiza una anonimización automática que puede ser incompleta. 
        Es responsabilidad exclusiva del profesional verificar el resultado antes de usar, 
        compartir o entregar cualquier documento procesado. El titular no asume responsabilidad 
        por datos personales no detectados ni por el incumplimiento de la normativa de protección 
        de datos derivado de una revisión insuficiente.
      </p>
    </div>
  </div>
);

// Header para páginas legales
const LegalHeader = ({ title }) => (
  <header className="legal-header">
    <div className="container">
      <Link to="/" className="back-link">
        <ArrowLeftIcon /> Volver al Anonimizador
      </Link>
      <div className="legal-header-content">
        <div className="legal-logo">
          <ShieldIcon />
        </div>
        <div>
          <span className="legal-brand">Tecno<span className="text-primary">abogado</span></span>
          <h1>{title}</h1>
        </div>
      </div>
    </div>
  </header>
);

// Footer para páginas legales
const LegalFooter = () => (
  <footer className="legal-footer">
    <div className="container">
      <p>© {new Date().getFullYear()} Tecnoabogado. Todos los derechos reservados.</p>
      <div className="legal-footer-links">
        <Link to="/aviso-legal">Aviso Legal</Link>
        <Link to="/politica-privacidad">Privacidad</Link>
        <Link to="/politica-cookies">Cookies</Link>
      </div>
    </div>
  </footer>
);

// ─── POLÍTICA DE PRIVACIDAD ─────────────────────────────────────────────────

export function PoliticaPrivacidad() {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Política de Privacidad | Tecnoabogado Anonimizador</title>
        <meta name="description" content="Política de privacidad de Tecnoabogado Anonimizador. Información sobre el tratamiento de datos personales conforme al RGPD y LOPDGDD." />
        <meta property="og:title" content="Política de Privacidad | Tecnoabogado Anonimizador" />
        <meta property="og:description" content="Información sobre el tratamiento de datos personales conforme al RGPD." />
        <link rel="canonical" href="https://anonimizador.tecnoabogado.com/politica-privacidad" />
      </Helmet>
      <LegalHeader title="Política de Privacidad" />
      
      <main className="legal-content">
        <div className="container">
          <AvisoDestacado />
          
          <section>
            <h2>1. Responsable del Tratamiento</h2>
            <ul className="info-list">
              <li><strong>Responsable:</strong> José Luis Vilaplana — Tecnoabogado</li>
              <li><strong>Dirección:</strong> Adriano 32-5º, 41001 Sevilla (España)</li>
              <li><strong>Teléfono:</strong> 954 564 602</li>
              <li><strong>Email:</strong> <a href="mailto:info@tecnoabogado.com">info@tecnoabogado.com</a></li>
            </ul>
          </section>

          <section>
            <h2>2. Datos que tratamos y finalidad</h2>
            <p>En el uso de la herramienta Tecnoabogado Anonimizador, se pueden tratar los siguientes datos:</p>
            
            <h3>Documentos procesados para anonimización</h3>
            <p>
              Los documentos que el usuario carga en la herramienta son procesados en un servidor seguro 
              ubicado en la Unión Europea. Los documentos <strong>no se almacenan</strong> después del procesamiento, 
              no son procesados por sistemas de inteligencia artificial de terceros y se eliminan inmediatamente 
              tras generar el resultado. El responsable del tratamiento no conserva el contenido de los documentos procesados.
            </p>

            <h3>Dirección de correo electrónico del usuario</h3>
            <p>
              Para el acceso a la herramienta, se solicita la dirección de correo electrónico del usuario 
              con la finalidad de llevar un registro de accesos y poder comunicar actualizaciones relevantes 
              del servicio. Esta dirección se trata con base en el consentimiento del usuario (art. 6.1.a RGPD) 
              y no se cede a terceros.
            </p>

            <h3>Datos de navegación (cookies técnicas)</h3>
            <p>
              Se utilizan cookies técnicas estrictamente necesarias para el funcionamiento del sitio web. 
              Consulte nuestra <Link to="/politica-cookies">Política de Cookies</Link> para más información.
            </p>
          </section>

          <section>
            <h2>3. Procesamiento seguro: privacidad por diseño</h2>
            <p>
              El diseño de esta herramienta aplica el principio de <strong>privacidad por diseño y por defecto</strong> (art. 25 RGPD): 
              los documentos se procesan sin almacenamiento permanente y se eliminan inmediatamente tras generar el resultado. 
              No se utiliza inteligencia artificial externa ni se envían datos a terceros.
            </p>
            <p>
              Esta arquitectura implica que el responsable del tratamiento no puede garantizar ni controlar 
              la calidad de la anonimización realizada, siendo el usuario el único que tiene acceso al 
              documento original y al resultado. Por ello, <strong>la verificación del resultado es una 
              responsabilidad exclusiva e ineludible del profesional usuario</strong>.
            </p>
          </section>

          <section>
            <h2>4. Base jurídica del tratamiento</h2>
            <p>El tratamiento de los datos se basa en:</p>
            <ul>
              <li><strong>Ejecución de un contrato o medidas precontractuales</strong> (art. 6.1.b RGPD): para la prestación del servicio de acceso a la herramienta.</li>
              <li><strong>Consentimiento</strong> (art. 6.1.a RGPD): para el tratamiento del correo electrónico del usuario y para el uso de cookies no técnicas, en su caso.</li>
              <li><strong>Interés legítimo</strong> (art. 6.1.f RGPD): para el mantenimiento y mejora del servicio.</li>
            </ul>
          </section>

          <section>
            <h2>5. Conservación de los datos</h2>
            <p>
              Los documentos procesados para anonimización <strong>no se almacenan</strong> en servidores del responsable, 
              eliminándose inmediatamente tras el procesamiento. El correo electrónico del usuario se conservará 
              mientras mantenga su acceso a la herramienta o hasta que solicite su supresión.
            </p>
            <p>Los datos de navegación se conservan durante el tiempo estrictamente necesario para la prestación del servicio.</p>
          </section>

          <section>
            <h2>6. Destinatarios</h2>
            <p>
              Los datos no se ceden ni se comunican a terceros, salvo obligación legal. La herramienta 
              no utiliza ninguna API externa ni modelo de inteligencia artificial de terceros para el 
              procesamiento de documentos. Todo el procesamiento de texto se realiza mediante código 
              propio ejecutado en servidores seguros de la Unión Europea.
            </p>
          </section>

          <section>
            <h2>7. Derechos de los interesados</h2>
            <p>
              En virtud del Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica 3/2018, 
              de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD), el usuario tiene derecho a:
            </p>
            <ul>
              <li>Acceder a sus datos personales</li>
              <li>Rectificar los datos inexactos</li>
              <li>Solicitar la supresión de sus datos</li>
              <li>Oponerse al tratamiento</li>
              <li>Solicitar la limitación del tratamiento</li>
              <li>Solicitar la portabilidad de los datos</li>
            </ul>
            <p>
              Para ejercer estos derechos, puede dirigirse a: <a href="mailto:info@tecnoabogado.com">info@tecnoabogado.com</a> o 
              por correo postal a: Adriano 32-5º, 41001 Sevilla.
            </p>
            <p>
              Asimismo, tiene derecho a presentar una reclamación ante la 
              Agencia Española de Protección de Datos (AEPD) en <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>.
            </p>
          </section>

          <section>
            <h2>8. Seguridad</h2>
            <p>
              José Luis Vilaplana — Tecnoabogado ha adoptado las medidas técnicas y organizativas necesarias 
              para garantizar la seguridad de los datos personales que trata. Los documentos procesados 
              se transmiten mediante conexión cifrada (HTTPS) y se eliminan inmediatamente tras el procesamiento.
            </p>
          </section>

          <section>
            <h2>9. Modificaciones</h2>
            <p>
              José Luis Vilaplana — Tecnoabogado se reserva el derecho a modificar la presente Política de 
              Privacidad para adaptarla a novedades legislativas o jurisprudenciales. En dichos supuestos, 
              se anunciará en esta página los cambios introducidos con razonable antelación a su puesta en práctica.
            </p>
            <p className="ultima-actualizacion">Última actualización: 1 de abril de 2026</p>
          </section>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// ─── POLÍTICA DE COOKIES ────────────────────────────────────────────────────

export function PoliticaCookies() {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Política de Cookies | Tecnoabogado Anonimizador</title>
        <meta name="description" content="Política de cookies de Tecnoabogado Anonimizador. Solo usamos cookies técnicas necesarias. No hay cookies de seguimiento ni publicidad." />
        <meta property="og:title" content="Política de Cookies | Tecnoabogado Anonimizador" />
        <meta property="og:description" content="Solo usamos cookies técnicas necesarias. No hay cookies de seguimiento." />
        <link rel="canonical" href="https://anonimizador.tecnoabogado.com/politica-cookies" />
      </Helmet>
      <LegalHeader title="Política de Cookies" />
      
      <main className="legal-content">
        <div className="container">
          <AvisoDestacado />
          
          <section>
            <h2>Qué son las cookies</h2>
            <p>
              Las cookies son pequeños archivos de texto que los sitios web almacenan en el dispositivo 
              del usuario cuando este los visita. Permiten que el sitio web recuerde las acciones y 
              preferencias del usuario durante un período de tiempo, de modo que no tenga que volver 
              a introducirlas cada vez que visite el sitio.
            </p>
          </section>

          <section>
            <h2>Procesamiento de documentos</h2>
            <p>
              El procesamiento de documentos realizado por la herramienta Tecnoabogado Anonimizador 
              <strong> no utiliza cookies</strong>. Los documentos se procesan en un servidor seguro sin almacenamiento 
              permanente y sin que ninguna cookie intervenga en dicho proceso. Las cookies descritas 
              en esta política afectan únicamente a la navegación por el sitio web, no al contenido 
              de los documentos procesados.
            </p>
          </section>

          <section>
            <h2>Cookies que utilizamos</h2>
            <p>Tecnoabogado Anonimizador utiliza únicamente cookies técnicas estrictamente necesarias para el funcionamiento del servicio:</p>
            
            <div className="table-responsive">
              <table className="cookies-table">
                <thead>
                  <tr>
                    <th>Cookie</th>
                    <th>Finalidad</th>
                    <th>Duración</th>
                    <th>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>ta_cookies_accepted</code></td>
                    <td>Recuerda la aceptación de condiciones de uso por parte del usuario</td>
                    <td>1 año</td>
                    <td>Técnica</td>
                  </tr>
                  <tr>
                    <td><code>ta_terminos_v2</code></td>
                    <td>Almacena la aceptación de términos legales</td>
                    <td>1 año</td>
                    <td>Técnica</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2>Cookies de terceros</h2>
            <p>
              Este sitio web <strong>no utiliza cookies de terceros</strong> con fines publicitarios, de seguimiento 
              o analítica de comportamiento. No se instalan cookies de Google Analytics, Facebook Pixel, 
              Meta Pixel ni ningún otro sistema de seguimiento externo. La herramienta no envía los 
              documentos procesados a ningún servicio externo ni utiliza inteligencia artificial de terceros.
            </p>
          </section>

          <section>
            <h2>Cómo gestionar las cookies</h2>
            <p>
              Puede configurar su navegador para rechazar todas las cookies o para que le avise cuando 
              se envíe una cookie. Sin embargo, si rechaza las cookies técnicas, es posible que algunas 
              funcionalidades del sitio no estén disponibles.
            </p>
            <ul className="browser-links">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Gestionar cookies en Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer">Gestionar cookies en Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Gestionar cookies en Safari</a></li>
              <li><a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Gestionar cookies en Microsoft Edge</a></li>
              <li><a href="https://www.perplexity.ai/settings/privacy" target="_blank" rel="noopener noreferrer">Gestionar cookies en Perplexity</a></li>
              <li><a href="https://www.facebook.com/help/instagram/615366948510230" target="_blank" rel="noopener noreferrer">Gestionar cookies en Meta (Facebook/Instagram)</a></li>
            </ul>
          </section>

          <section>
            <h2>Base legal</h2>
            <p>
              El uso de cookies técnicas se basa en el <strong>interés legítimo</strong> del responsable del tratamiento 
              (art. 6.1.f RGPD) al ser estrictamente necesarias para la prestación del servicio. Para 
              cualquier otra cookie, la base legal es el consentimiento del usuario (art. 6.1.a RGPD).
            </p>
          </section>

          <section>
            <h2>Más información</h2>
            <p>
              Para más información sobre el tratamiento de sus datos personales, consulte nuestra 
              <Link to="/politica-privacidad"> Política de Privacidad</Link> o contacte con nosotros 
              en <a href="mailto:info@tecnoabogado.com">info@tecnoabogado.com</a>.
            </p>
            <p className="ultima-actualizacion">Última actualización: 1 de abril de 2026</p>
          </section>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// ─── AVISO LEGAL ────────────────────────────────────────────────────────────

export function AvisoLegal() {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Aviso Legal | Tecnoabogado Anonimizador</title>
        <meta name="description" content="Aviso legal de Tecnoabogado Anonimizador. Información sobre el titular, condiciones de uso, limitaciones de responsabilidad y propiedad intelectual." />
        <meta property="og:title" content="Aviso Legal | Tecnoabogado Anonimizador" />
        <meta property="og:description" content="Condiciones de uso, limitaciones de responsabilidad y propiedad intelectual." />
        <link rel="canonical" href="https://anonimizador.tecnoabogado.com/aviso-legal" />
      </Helmet>
      <LegalHeader title="Aviso Legal" />
      
      <main className="legal-content">
        <div className="container">
          <AvisoDestacado />
          
          <section>
            <h2>1. Datos identificativos</h2>
            <p>En cumplimiento del deber de información recogido en el artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE), se exponen los siguientes datos:</p>
            <ul className="info-list">
              <li><strong>Titular:</strong> José Luis Vilaplana — Tecnoabogado</li>
              <li><strong>Domicilio:</strong> Adriano 32-5º, 41001 Sevilla (España)</li>
              <li><strong>Teléfono:</strong> 954 564 602</li>
              <li><strong>Email:</strong> <a href="mailto:info@tecnoabogado.com">info@tecnoabogado.com</a></li>
              <li><strong>Actividad:</strong> Servicios tecnológicos para profesionales jurídicos</li>
            </ul>
          </section>

          <section>
            <h2>2. Objeto y ámbito de aplicación</h2>
            <p>
              El presente Aviso Legal regula el acceso y uso del sitio web y de la herramienta 
              Tecnoabogado Anonimizador (en adelante, "el Servicio"), que José Luis Vilaplana — 
              Tecnoabogado pone a disposición de los usuarios de Internet.
            </p>
            <p>
              El acceso al Servicio atribuye la condición de usuario e implica la aceptación plena 
              y sin reservas de todas las disposiciones incluidas en este Aviso Legal, así como en 
              la <Link to="/politica-privacidad">Política de Privacidad</Link> y la <Link to="/politica-cookies">Política de Cookies</Link>.
            </p>
          </section>

          <section>
            <h2>3. Descripción del Servicio</h2>
            <p>
              Tecnoabogado Anonimizador es una herramienta que permite la detección y sustitución 
              automática de datos personales en documentos de texto mediante algoritmos de procesamiento 
              de lenguaje natural (NLP) basados en patrones y expresiones regulares.
            </p>
            <p>
              <strong>El Servicio NO utiliza inteligencia artificial externa</strong> ni envía los documentos 
              a terceros para su procesamiento. Los documentos se procesan en servidores seguros 
              ubicados en la Unión Europea y <strong>no se almacenan</strong> tras el procesamiento.
            </p>
          </section>

          <section>
            <h2>4. Limitaciones y exención de responsabilidad</h2>
            <div className="aviso-importante">
              <AlertTriangleIcon />
              <div>
                <p>
                  <strong>AVISO IMPORTANTE:</strong> La herramienta Tecnoabogado Anonimizador realiza una 
                  anonimización automática basada en patrones que <strong>puede ser incompleta o imprecisa</strong>. 
                  El titular del Servicio:
                </p>
                <ul>
                  <li><strong>NO garantiza</strong> la detección de todos los datos personales presentes en un documento.</li>
                  <li><strong>NO garantiza</strong> el cumplimiento automático del RGPD, la LOPDGDD ni ninguna otra normativa.</li>
                  <li><strong>NO asume responsabilidad</strong> por los daños derivados de una anonimización incompleta.</li>
                  <li><strong>NO asume responsabilidad</strong> por el uso que el usuario haga de los documentos procesados.</li>
                </ul>
              </div>
            </div>
            <p>
              Es <strong>responsabilidad exclusiva del usuario profesional</strong>:
            </p>
            <ul>
              <li>Verificar exhaustivamente el resultado de la anonimización antes de usar el documento.</li>
              <li>Asegurar que no quedan datos personales identificables en el documento final.</li>
              <li>Cumplir con la normativa de protección de datos aplicable en cada caso.</li>
              <li>Adoptar las medidas adicionales de anonimización que sean necesarias.</li>
            </ul>
          </section>

          <section>
            <h2>5. Propiedad intelectual e industrial</h2>
            <p>
              Todos los contenidos del sitio web, incluyendo textos, fotografías, gráficos, imágenes, 
              iconos, tecnología, software, así como el diseño gráfico y códigos fuente, constituyen 
              una obra cuya propiedad corresponde a José Luis Vilaplana — Tecnoabogado, sin que puedan 
              entenderse cedidos al usuario ninguno de los derechos de explotación sobre los mismos 
              más allá de lo estrictamente necesario para el correcto uso del Servicio.
            </p>
          </section>

          <section>
            <h2>6. Legislación aplicable y jurisdicción</h2>
            <p>
              Las relaciones entre José Luis Vilaplana — Tecnoabogado y el usuario se regirán por 
              la normativa española vigente y cualquier controversia se someterá a los Juzgados 
              y Tribunales de la ciudad de Sevilla, salvo que la normativa de consumidores establezca 
              otro fuero.
            </p>
          </section>

          <section>
            <h2>7. Modificaciones</h2>
            <p>
              José Luis Vilaplana — Tecnoabogado se reserva el derecho de efectuar sin previo aviso 
              las modificaciones que considere oportunas en el Servicio, pudiendo cambiar, suprimir 
              o añadir tanto los contenidos y servicios que se presten a través del mismo como la 
              forma en la que éstos aparezcan presentados o localizados.
            </p>
            <p className="ultima-actualizacion">Última actualización: 1 de abril de 2026</p>
          </section>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}
